import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Icon } from '@iconify/react'
import {
  withStyles,
  Paper,
  OutlinedInput,
  InputAdornment,
  CircularProgress,
  Typography,
  Button,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import styles from './styles'
import { iconList } from './iconList'
import { CoreIconButton } from '../core'

// Set constant to display exactly 50 icons
const ICONS_PER_PAGE = 30

const SelectedIcon = ({ classes, target, editor, onIconSelect }) => {
  const [selectedIcon, setSelectedIcon] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [icons, setIcons] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalResults, setTotalResults] = useState(0)

  // Load icons only once
  useEffect(() => {
    setIsLoading(true)

    const loadIcons = setTimeout(() => {
      // Pre-format icons only once when component loads
      const formattedIcons = iconList.map(icon => `hugeicons:${icon}`)
      setIcons(formattedIcons)
      setTotalResults(formattedIcons.length)
      setIsLoading(false)
    }, 1000)

    return () => clearTimeout(loadIcons)
  }, [])

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm])

  // Debounce search to avoid filtering on every keystroke
  const handleSearchChange = useCallback(e => {
    const value = e.target.value
    setSearchTerm(value)
  }, [])

  // Memoize filtered icons to prevent unnecessary re-renders
  const filteredIcons = useMemo(() => {
    if (!searchTerm.trim()) {
      setTotalResults(icons.length)
      return icons
    }

    // Search is performed on ALL icons
    const filtered = icons.filter(icon =>
      icon.toLowerCase().includes(searchTerm.toLowerCase()),
    )

    setTotalResults(filtered.length)
    return filtered
  }, [icons, searchTerm])

  // Get current page of exactly 50 icons
  const currentIcons = useMemo(() => {
    const startIndex = (currentPage - 1) * ICONS_PER_PAGE
    return filteredIcons.slice(startIndex, startIndex + ICONS_PER_PAGE)
  }, [filteredIcons, currentPage])

  // Calculate total pages
  const totalPages = Math.ceil(totalResults / ICONS_PER_PAGE)

  const handleIconSelect = useCallback(
    icon => {
      setSelectedIcon(icon)
      onIconSelect(icon)
    },
    [onIconSelect],
  )

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1)
    }
  }

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1)
    }
  }

  if (isLoading) {
    return (
      <div className={classes.loading}>
        <CircularProgress size={40} />
      </div>
    )
  }

  return (
    <div className={classes.container}>
      <Paper className={classes.root}>
        <OutlinedInput
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search Icon"
          className={classes.input}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon className={classes.searchIcon} />
            </InputAdornment>
          }
        />
      </Paper>

      <div className={classes.grid}>
        {currentIcons.map(icon => (
          <button
            key={icon}
            onClick={() => handleIconSelect(icon)}
            className={`${classes.button} ${
              selectedIcon === icon ? classes.selected : ''
            }`}
            title={icon.replace('hugeicons:', '')}
          >
            <Icon icon={icon} width={24} height={24} />
          </button>
        ))}
      </div>

      {totalPages > 1 && (
        <div className={classes.pagination}>
          <CoreIconButton
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            size="small"
            variant="text"
            className={classes.paginationButton}
          >
            <Icon icon="hugeicons:arrow-left-02" />{' '}
          </CoreIconButton>
          <Typography variant="body2">
            {`Page ${currentPage} of ${totalPages}`}
          </Typography>

          <CoreIconButton
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            size="small"
            variant="text"
            className={classes.paginationButton}
          >
            <Icon icon="hugeicons:arrow-right-02" />{' '}
          </CoreIconButton>
        </div>
      )}
    </div>
  )
}

export default withStyles(styles)(SelectedIcon)
